import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import Extent from '@arcgis/core/geometry/Extent';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { MapSymbolFactory } from './map-symbol-factory.service';
import { ImageProcessingService } from './image-processing.service';
import { MapDrilldownFilter } from '../../modules/projects/core/mapped-reports/models/map-drilldown-filter.model';

/**
 * Configuration for drilldown mode behavior
 */
export interface DrilldownConfig {
  isFullScreen: boolean;
  isSingleDayMode?: boolean;
  layerSettings: {
    siteBoundary: boolean;
    picture: boolean;
    asBuilt: boolean;
    bidItem: boolean;
    activity: boolean;
    delivery: boolean;
  };
  /** Skip restoration of markers and zoom - used when exiting fullscreen */
  skipRestoration?: boolean;
}

/**
 * State stored during drilldown mode
 */
export interface DrilldownState {
  previousMapExtent: Extent | null;
  previousMapZoom: number | null;
  isInDrilldownMode: boolean;
  noLocationPictures: any[];
  noLocationCount: number;
}

/**
 * Context required for drilldown operations
 */
export interface DrilldownContext {
  mapView: __esri.MapView;
  unifiedMarkersLayer: __esri.FeatureLayer | null;
  asBuiltLayer: __esri.GraphicsLayer | null;
  projectBoundaryLayer: __esri.GraphicsLayer | null;
}

/**
 * Result from picture collection during drilldown filtering
 */
interface PictureCollectionResult {
  pictureIds: string[];
  noLocationPictures: any[];
  picturesWithLocation: any[];
}

/**
 * DrilldownModeService
 *
 * Handles all drilldown mode logic for the map component:
 * - Entering drilldown mode (filtering to show only selected item)
 * - Exiting drilldown mode (restoring all layers)
 * - Zooming to filtered item extent
 * - Managing temporary layers for filtered views
 *
 * Extracted from SitesLocationMapComponent to improve maintainability
 * (Phase 4.8 refactor)
 */
@Injectable({
  providedIn: 'root'
})
export class DrilldownModeService {
  // Internal state
  private previousMapExtent: Extent | null = null;
  private previousMapZoom: number | null = null;
  private tempFilteredAsBuiltLayer: GraphicsLayer | null = null;
  private tempFilteredPictureLayer: any = null;
  private drilldownNoLocationPictures: any[] = [];
  private drilldownNoLocationCount: number = 0;
  private hiddenLayerIds: string[] = [];

  // Observable to signal when drilldown exit is in progress
  // Components should skip map reinitialization while this is true
  private _drilldownExitInProgress$ = new BehaviorSubject<boolean>(false);
  public drilldownExitInProgress$ = this._drilldownExitInProgress$.asObservable();

  /**
   * Synchronous check if drilldown exit is currently in progress
   * Use this in callbacks where withLatestFrom isn't practical
   */
  get isExitInProgress(): boolean {
    return this._drilldownExitInProgress$.getValue();
  }

  // Maps for picture data lookup
  private markerPicturesMap: Map<string, any[]> = new Map();

  constructor(
    private mapSymbolFactory: MapSymbolFactory,
    private imageProcessingService: ImageProcessingService
  ) {}

  /**
   * Get current drilldown state
   */
  getState(): DrilldownState {
    return {
      previousMapExtent: this.previousMapExtent,
      previousMapZoom: this.previousMapZoom,
      isInDrilldownMode: this.previousMapExtent !== null,
      noLocationPictures: this.drilldownNoLocationPictures,
      noLocationCount: this.drilldownNoLocationCount
    };
  }

  /**
   * Get no-location pictures from current drilldown
   */
  getNoLocationPictures(): any[] {
    return this.drilldownNoLocationPictures;
  }

  /**
   * Get no-location picture count
   */
  getNoLocationCount(): number {
    return this.drilldownNoLocationCount;
  }

  /**
   * Sync external marker pictures map (bidirectional sync with component)
   */
  syncMarkerPicturesMap(map: Map<string, any[]>): void {
    this.markerPicturesMap = map;
  }

  /**
   * Get pictures for a marker uuid from the drilldown pictures map
   */
  getPicturesForMarker(uuid: string): any[] | undefined {
    return this.markerPicturesMap.get(uuid);
  }

  /**
   * Enter drilldown mode for a specific item
   * - Store current map extent/zoom
   * - Filter layers to show only selected item
   * - Zoom to extent of item elements
   */
  async enterDrilldownMode(
    bidItemData: any,
    context: DrilldownContext,
    config: DrilldownConfig
  ): Promise<void> {
    if (!context.mapView) {
      return;
    }

    // Store current extent and zoom
    this.previousMapExtent = context.mapView.extent.clone();
    this.previousMapZoom = context.mapView.zoom;

    // Filter layers to show only selected item
    await this.filterLayersForItem(bidItemData, context, config);

    // Zoom to extent of item elements
    await this.zoomToItemExtent(bidItemData, context, config);
  }

  /**
   * Enter drilldown mode for a SET of report_bid_item ids (mapped-reports flow).
   *
   * Unlike enterDrilldownMode (single item), this filters the unified markers
   * layer to ALL of the supplied report_bid_item_ids via a single IN (...) clause
   * and reuses the existing visual filter mechanism (featureEffect). It does NOT
   * mutate the single-item drilldown path in any way.
   *
   * NOTE on featurePredicate: the existing drilldown applies only ArcGIS
   * definition/feature-effect where-clauses against the unified layer; there is no
   * per-feature JavaScript pass in this service. The filter.featurePredicate is a
   * client-side function over the GeoJSON features inside each report_bid_item's
   * geometry FeatureCollection and is therefore applied upstream by the mapped-report
   * map pane when it builds the geometry it feeds to the map. Here we only wire the
   * server-style IN (...) id clause.
   */
  async enterDrilldownModeWithFilter(
    filter: MapDrilldownFilter,
    context: DrilldownContext
  ): Promise<void> {
    if (!context.mapView) {
      return;
    }

    // Store current extent and zoom (mirrors enterDrilldownMode)
    this.previousMapExtent = context.mapView.extent.clone();
    this.previousMapZoom = context.mapView.zoom;

    const whereClause = this.buildReportBidItemInClause(filter?.reportBidItemIds ?? []);

    // Apply the IN(...) clause to the unified markers layer using the existing
    // visual-filter helper so behavior matches the single-item path.
    if (context.mapView.map?.layers) {
      context.mapView.map.layers.forEach((layer: any) => {
        if (layer.id === 'unified-markers-layer') {
          this.filterUnifiedLayer(layer, whereClause);
        }
      });
    }
  }

  /**
   * Build a where clause that matches MANY report_bid_item ids.
   * Empty list -> "1=0" so nothing is shown.
   */
  private buildReportBidItemInClause(reportBidItemIds: string[]): string {
    if (!reportBidItemIds || reportBidItemIds.length === 0) {
      return '1=0';
    }
    const quoted = reportBidItemIds.map(id => `'${id.replace(/'/g, "''")}'`).join(',');
    return `report_bid_item_id IN (${quoted})`;
  }

  /**
   * Exit drilldown mode
   * - Clear temporary state
   * - Restore all layers (unless skipRestoration is true)
   * - Return to previous extent/zoom (unless skipRestoration is true)
   */
  async exitDrilldownMode(
    context: DrilldownContext,
    config: DrilldownConfig
  ): Promise<void> {
    if (!context.mapView) {
      return;
    }

    // Signal that drilldown exit is in progress - components should skip map reinitialization
    this._drilldownExitInProgress$.next(true);

    // Clear drilldown no-location pictures
    this.drilldownNoLocationPictures = [];
    this.drilldownNoLocationCount = 0;

    // Remove temp layers FIRST
    await this.removeTempLayers(context);

    // If skipRestoration is true, skip marker restoration and zoom animation
    // This is used when exiting fullscreen to avoid marker flash
    if (config.skipRestoration) {
      // Clear stored state
      this.previousMapExtent = null;
      this.previousMapZoom = null;

      // Signal that drilldown exit is complete
      this._drilldownExitInProgress$.next(false);
      return;
    }

    // Phase 1: Restore definition expressions and visibility (but NOT clustering yet)
    this.restoreLayerExpressionsOnly(context, config);

    // Zoom back to previous extent
    if (this.previousMapExtent && this.previousMapZoom !== null) {
      await context.mapView.goTo({
        target: this.previousMapExtent,
        zoom: this.previousMapZoom
      }, {
        duration: 800,
        easing: 'ease-in-out'
      });
    }

    // Wait for mapView to be completely stationary after zoom
    if (!context.mapView.stationary) {
      await new Promise<void>(resolve => {
        const handle = context.mapView.watch('stationary', (stationary: boolean) => {
          if (stationary) {
            handle.remove();
            resolve();
          }
        });
      });
    }

    // Wait for updating to be false
    if (context.mapView.updating) {
      await new Promise<void>(resolve => {
        const handle = context.mapView.watch('updating', (updating: boolean) => {
          if (!updating) {
            handle.remove();
            resolve();
          }
        });
      });
    }

    // Phase 2: NOW enable clustering at the correct extent
    if (context.unifiedMarkersLayer) {
      context.unifiedMarkersLayer.featureReduction = this.getClusteringConfig();

      // Wait for layer to update with new clustering
      try {
        const layerView = await context.mapView.whenLayerView(context.unifiedMarkersLayer);
        if (layerView.updating) {
          await new Promise<void>(resolve => {
            const handle = layerView.watch('updating', (updating: boolean) => {
              if (!updating) {
                handle.remove();
                resolve();
              }
            });
          });
        }
      } catch (e) {
        // Ignore layerView errors during exit
      }
    }

    // Restore other layers
    this.restoreOtherLayers(context, config);

    // Clear stored state
    this.previousMapExtent = null;
    this.previousMapZoom = null;

    // Signal that drilldown exit is complete - components can now reinitialize if needed
    this._drilldownExitInProgress$.next(false);
  }

  /**
   * Phase 1: Restore only definition expressions and visibility (not clustering)
   */
  private restoreLayerExpressionsOnly(
    context: DrilldownContext,
    config: DrilldownConfig
  ): void {
    if (!context.mapView?.map) return;

    context.mapView.map.layers.forEach((layer: any) => {
      if (layer.id === 'unified-markers-layer') {
        // Keep clustering disabled during zoom
        layer.featureReduction = null;

        // Clear the drilldown's visual filter (set in filterUnifiedLayer). All
        // markers become visible again; the base definitionExpression below is
        // unchanged from before drilldown, so this is a no-op re-query at worst.
        layer.featureEffect = null;

        // Restore definition expression
        if (config.layerSettings.picture) {
          layer.definitionExpression = "markerType = 'bid_item' OR markerType = 'activity' OR markerType = 'delivery' OR markerType = 'form' OR markerType = 'picture' OR markerType = 'multi_item_location'";
        } else {
          layer.definitionExpression = "markerType = 'bid_item' OR markerType = 'activity' OR markerType = 'delivery' OR markerType = 'form' OR markerType = 'multi_item_location'";
        }

        // Restore visibility (visible in fullscreen or single-day mode)
        layer.visible = config.isFullScreen || !!config.isSingleDayMode;
      }
    });
  }

  /**
   * Phase 2: Restore other layers after main layer is done
   */
  private restoreOtherLayers(
    context: DrilldownContext,
    config: DrilldownConfig
  ): void {
    if (!context.mapView?.map) return;

    // Restore ALL layers that were hidden during filterLayersForItem
    context.mapView.map.layers.forEach((layer: any) => {
      if (this.hiddenLayerIds.includes(layer.id)) {
        layer.visible = true;
      }
    });
    this.hiddenLayerIds = [];

    // Restore as-built layer visibility (and its parent GroupLayer)
    if (context.asBuiltLayer) {
      const parentLayer = context.asBuiltLayer.parent;

      // CRITICAL: Restore parent GroupLayer visibility first
      if (parentLayer && 'visible' in parentLayer) {
        (parentLayer as any).visible = true;
      }

      context.asBuiltLayer.visible = config.layerSettings.asBuilt;
    }

    // Restore site boundaries opacity AND visibility (and its parent GroupLayer)
    if (context.projectBoundaryLayer) {
      const parentLayer = context.projectBoundaryLayer.parent;

      // CRITICAL: Restore parent GroupLayer visibility first
      if (parentLayer && 'visible' in parentLayer) {
        (parentLayer as any).visible = true;
      }

      context.projectBoundaryLayer.opacity = 1.0;
      context.projectBoundaryLayer.visible = true;
    }
  }

  /**
   * Remove temporary drilldown layers
   */
  private async removeTempLayers(context: DrilldownContext): Promise<void> {
    // Remove temp as-built layer
    if (this.tempFilteredAsBuiltLayer && context.mapView?.map) {
      context.mapView.map.remove(this.tempFilteredAsBuiltLayer);
      this.tempFilteredAsBuiltLayer = null;
    }

    // Remove temp picture layer and clean up map entries
    if (this.tempFilteredPictureLayer && context.mapView?.map) {
      if (this.tempFilteredPictureLayer.graphics?.forEach) {
        this.tempFilteredPictureLayer.graphics.forEach((graphic: any) => {
          if (graphic.attributes?.uuid) {
            this.markerPicturesMap.delete(graphic.attributes.uuid);
          }
        });
      }
      context.mapView.map.remove(this.tempFilteredPictureLayer);
      this.tempFilteredPictureLayer = null;
    }
  }

  /**
   * Restore layer settings after drilldown (delegates to phased methods)
   */
  private async restoreLayerSettings(
    context: DrilldownContext,
    config: DrilldownConfig
  ): Promise<void> {
    this.restoreLayerExpressionsOnly(context, config);
    if (context.unifiedMarkersLayer) {
      context.unifiedMarkersLayer.featureReduction = this.getClusteringConfig();
    }
    this.restoreOtherLayers(context, config);
  }

  /**
   * Determine item type from data structure
   */
  /**
   * True when the item carries a station_range with at least a FROM or TO value —
   * i.e. the dedicated station-range layer will draw + zoom to it. Mirrors the
   * "is there anything to draw" check used when rendering the range.
   */
  private hasUsableStationRange(item: any): boolean {
    const sr = item?.station_range || item?.stationRange;
    if (!sr) {
      return false;
    }
    const hasValues = (obj: any) =>
      obj && Object.values(obj).some(v => v !== null && v !== undefined && v !== '');
    return hasValues(sr.from) || hasValues(sr.to);
  }

  private determineItemType(bidItemData: any): { isActivity: boolean; isDelivery: boolean; isBidItem: boolean; isForm: boolean } {
    if (bidItemData?._mapItemType === 'activity') {
      return { isActivity: true, isDelivery: false, isBidItem: false, isForm: false };
    }
    if (bidItemData?._mapItemType === 'delivery') {
      return { isActivity: false, isDelivery: true, isBidItem: false, isForm: false };
    }
    if (bidItemData?._mapItemType === 'forms') {
      return { isActivity: false, isDelivery: false, isBidItem: false, isForm: true };
    }
    if (bidItemData?._mapItemType === 'bid_item') {
      return { isActivity: false, isDelivery: false, isBidItem: true, isForm: false };
    }

    // Control form: has the control_form definition attached (forms-on-map markers)
    const isForm = !!(bidItemData?.control_form || bidItemData?.control_form_id);
    // Activity: has 'type' field (activity type like 'labor', 'equipment', etc.)
    // Delivery: has delivery-specific fields. Some valid deliveries are bid-item-linked
    // without an expanded material object, so don't rely on `material` alone.
    // Bid Item: has 'bid_item' field but NO 'material' field, OR has 'allLineItems' array
    const isDelivery = !isForm && !!(
      bidItemData?.delivery_id ||
      bidItemData?.material ||
      bidItemData?.material_id ||
      bidItemData?.received !== undefined ||
      bidItemData?.used !== undefined
    );
    const isActivity = !isForm && bidItemData?.type !== undefined && !bidItemData?.bid_item && !isDelivery;
    const isBidItem = !isForm && !isActivity && !isDelivery && (!!bidItemData?.bid_item || !!bidItemData?.allLineItems);

    return { isActivity, isDelivery, isBidItem, isForm };
  }

  /**
   * Collect pictures from item data, separating by location
   */
  private collectPictures(bidItemData: any, isActivity: boolean, isDelivery: boolean, isForm: boolean = false): PictureCollectionResult {
    const pictureIds: string[] = [];
    const noLocationPictures: any[] = [];
    const picturesWithLocation: any[] = [];

    if (isActivity || isDelivery || isForm) {
      // For activities, deliveries and forms, pictures are directly on the object
      if (bidItemData.pictures && Array.isArray(bidItemData.pictures)) {
        bidItemData.pictures.forEach((picture: any) => {
          if (picture.id) {
            if (picture.latitude && picture.longitude) {
              pictureIds.push(picture.id);
              picturesWithLocation.push(picture);
            } else {
              noLocationPictures.push(picture);
            }
          }
        });
      }
    } else {
      // For bid items, pictures are in allLineItems
      if (bidItemData.allLineItems && Array.isArray(bidItemData.allLineItems)) {
        bidItemData.allLineItems.forEach((lineItem: any) => {
          if (lineItem.pictures && Array.isArray(lineItem.pictures)) {
            lineItem.pictures.forEach((picture: any) => {
              if (picture.id) {
                if (picture.latitude && picture.longitude) {
                  pictureIds.push(picture.id);
                  // Tag the owning line so the open line's photos can be told apart on the map
                  picturesWithLocation.push({ ...picture, _line_item_id: lineItem.id });
                } else {
                  noLocationPictures.push(picture);
                }
              }
            });
          }
        });
      }
    }

    return { pictureIds, noLocationPictures, picturesWithLocation };
  }

  /**
   * Check if a picture exists at the same location as the marker
   */
  private hasPictureAtSameLocation(
    bidItemData: any,
    picturesWithLocation: any[]
  ): boolean {
    if (picturesWithLocation.length === 0) {
      return false;
    }

    // Get marker location - from item's own coords, or derived from pictures
    let markerLat = bidItemData?.latitude;
    let markerLon = bidItemData?.longitude;

    // If item doesn't have its own location, derive from pictures (center point)
    if (!markerLat || !markerLon) {
      const sumLat = picturesWithLocation.reduce((sum, p) => sum + parseFloat(p.latitude), 0);
      const sumLon = picturesWithLocation.reduce((sum, p) => sum + parseFloat(p.longitude), 0);
      markerLat = sumLat / picturesWithLocation.length;
      markerLon = sumLon / picturesWithLocation.length;
    }

    // Check if any picture is at the same location as marker (using 6 decimal place precision ~0.1m)
    return picturesWithLocation.some((pic: any) => {
      const picLat = parseFloat(pic.latitude).toFixed(6);
      const picLon = parseFloat(pic.longitude).toFixed(6);
      const mLat = parseFloat(markerLat).toFixed(6);
      const mLon = parseFloat(markerLon).toFixed(6);
      return picLat === mLat && picLon === mLon;
    });
  }

  /**
   * Build filter expression for unified markers layer
   */
  private buildFilterExpression(
    itemId: string,
    isActivity: boolean,
    isDelivery: boolean,
    hasPictureAtSameLocation: boolean,
    isForm: boolean = false
  ): string {
    if (hasPictureAtSameLocation) {
      // Hide the item marker to avoid overlap with picture
      return `uuid = 'DRILLDOWN_HIDE_MARKER'`;
    }

    // Show the item marker using appropriate ID field
    if (isActivity) {
      return `uuid = '${itemId}' OR activity_id = '${itemId}'`;
    } else if (isDelivery) {
      return `uuid = '${itemId}' OR delivery_id = '${itemId}'`;
    } else if (isForm) {
      return `uuid = '${itemId}' OR form_id = '${itemId}'`;
    } else {
      return `uuid = '${itemId}' OR report_bid_item_id = '${itemId}'`;
    }
  }

  /**
   * Filter unified markers layer for drilldown mode
   */
  private filterUnifiedLayer(
    layer: any,
    filterExpression: string
  ): void {
    // Filter VISUALLY via featureEffect instead of mutating definitionExpression.
    // The unified layer is a client-side FeatureLayer; changing its
    // definitionExpression forces a full source re-query, and the tile requests
    // fired by the drilldown zoom animation race that rebuild — the query engine
    // is transiently null, producing the [esri … executeQuery] tile errors.
    // featureEffect hides the non-matching markers (opacity 0) without re-querying
    // the source, so the base definitionExpression stays stable and no errors fire.
    layer.featureEffect = {
      filter: { where: filterExpression },
      excludedEffect: 'opacity(0)',
    };
    layer.visible = true; // Force show even if layer is toggled off
    layer.featureReduction = null; // Disable clustering in drilldown mode
  }

  /**
   * Create temporary as-built layer with filtered graphics
   */
  private async createFilteredAsBuiltLayer(
    bidItemData: any,
    context: DrilldownContext
  ): Promise<void> {
    if (!context.asBuiltLayer) {
      return;
    }

    // Hide the original layer
    context.asBuiltLayer.visible = false;

    const { isActivity, isDelivery } = this.determineItemType(bidItemData);

    // Collect all ids that can be attached to GPS as-built graphics.
    const itemId = bidItemData?.id;
    const allReportBidItemIds: string[] = [itemId];
    if (bidItemData.allLineItems && Array.isArray(bidItemData.allLineItems)) {
      bidItemData.allLineItems.forEach((lineItem: any) => {
        if (lineItem.id && !allReportBidItemIds.includes(lineItem.id)) {
          allReportBidItemIds.push(lineItem.id);
        }
      });
    }

    // The as-built layer holds ONLY GPS as-built geometries (bid items). Station
    // ranges live on the dedicated station-range layer, not here.
    const ownsGraphic = (graphic: any) => {
      if (isActivity) {
        return graphic.attributes?.report_activity_id === itemId;
      }
      if (isDelivery) {
        return graphic.attributes?.report_material_delivery_id === itemId;
      }
      return allReportBidItemIds.includes(graphic.attributes?.report_bid_item_id);
    };

    const allGraphics = context.asBuiltLayer.graphics.toArray();
    const matchingGraphics = allGraphics.filter(ownsGraphic);

    if (matchingGraphics.length > 0) {
      this.tempFilteredAsBuiltLayer = new GraphicsLayer({
        id: 'temp-filtered-asbuilt-layer',
        opacity: 1,
        visible: true
      });

      // Copy the renderer from the original layer to preserve site colors
      if ((context.asBuiltLayer as any).renderer) {
        (this.tempFilteredAsBuiltLayer as any).renderer = (context.asBuiltLayer as any).renderer.clone();
      }

      // Add matching graphics to the temp layer
      matchingGraphics.forEach((graphic: any) => {
        this.tempFilteredAsBuiltLayer!.add(graphic.clone());
      });

      // Add the temp layer to the map below unified markers
      if (context.mapView?.map) {
        const unifiedLayerIndex = context.unifiedMarkersLayer
          ? context.mapView.map.layers.indexOf(context.unifiedMarkersLayer)
          : -1;

        if (unifiedLayerIndex !== -1) {
          context.mapView.map.add(this.tempFilteredAsBuiltLayer, unifiedLayerIndex);
        } else {
          context.mapView.map.add(this.tempFilteredAsBuiltLayer);
        }
      }
    }
  }

  /**
   * Comma-wrapped owning line ids (",id1,id2,") so a line id matches exactly as ",<id>,".
   */
  private toLineIdsAttribute(pictures: { _line_item_id?: string }[]): string {
    const ids = Array.from(new Set(pictures.map(p => p._line_item_id).filter(Boolean)));
    return ids.length ? `,${ids.join(',')},` : '';
  }

  /**
   * Group pictures by location (for same-location markers)
   */
  private groupPicturesByLocation(pictures: any[]): Map<string, any[]> {
    const locationMap = new Map<string, any[]>();

    pictures.forEach(picture => {
      if (picture.latitude && picture.longitude) {
        // Use 5 decimal precision (~1.1m) for grouping
        const latKey = parseFloat(picture.latitude).toFixed(5);
        const lonKey = parseFloat(picture.longitude).toFixed(5);
        const locationKey = `${latKey},${lonKey}`;

        if (!locationMap.has(locationKey)) {
          locationMap.set(locationKey, []);
        }
        locationMap.get(locationKey)!.push(picture);
      }
    });

    return locationMap;
  }

  /**
   * Create temporary picture layer with filtered pictures
   */
  private async createFilteredPictureLayer(
    picturesWithLocation: any[],
    context: DrilldownContext
  ): Promise<void> {
    if (picturesWithLocation.length === 0) {
      return;
    }

    // Group pictures by location
    const locationGroups = this.groupPicturesByLocation(picturesWithLocation);

    // Separate same-location groups from individual pictures
    const sameLocationGroups: Array<{ location: string; pictures: any[] }> = [];
    const individualPictures: any[] = [];

    locationGroups.forEach((pictures, locationKey) => {
      if (pictures.length >= 2) {
        sameLocationGroups.push({ location: locationKey, pictures });
      } else {
        individualPictures.push(...pictures);
      }
    });

    const picturePromises: Promise<any>[] = [];

    // Process same-location groups with count badges
    sameLocationGroups.forEach((group, index) => {
      const picturePromise = (async () => {
        try {
          const firstPicture = group.pictures[0];
          // Support multiple field names: thumb_url, original_url, preview, preview_url
          const imageUrl = firstPicture.thumb_url || firstPicture.original_url || firstPicture.preview || firstPicture.preview_url;
          const circularImageUrl = await this.imageProcessingService.createCircularImage(imageUrl, 35);
          const avgDirection = group.pictures.reduce((sum: number, p: any) => sum + (p.direction || 0), 0) / group.pictures.length;

          const markerUuid = `same-location-${group.location}`;

          // Normalize pictures to ensure thumb_url/original_url are set for the overlay
          const normalizedPictures = group.pictures.map((pic: any) => ({
            ...pic,
            thumb_url: pic.thumb_url || pic.preview || pic.preview_url,
            original_url: pic.original_url || pic.original || pic.preview || pic.preview_url
          }));
          // Store pictures in map for retrieval on click
          this.markerPicturesMap.set(markerUuid, normalizedPictures);

          const point = {
            type: 'point',
            longitude: parseFloat(firstPicture.longitude),
            latitude: parseFloat(firstPicture.latitude)
          };

          const symbol = this.mapSymbolFactory.createSameLocationMarkerSymbol(
            circularImageUrl,
            avgDirection,
            group.pictures.length,
            markerUuid
          );

          const Graphic = (await import('@arcgis/core/Graphic')).default;
          return new Graphic({
            geometry: point as any,
            symbol: symbol as any,
            attributes: {
              ObjectID: index + 1,
              uuid: markerUuid,
              markerType: 'picture',
              is_same_location: 1,
              count: group.pictures.length,
              picture_uuids: group.pictures.map((p: any) => p.id || p.uuid).join(','),
              line_ids: this.toLineIdsAttribute(group.pictures),
              fade: 0
            }
          });
        } catch (error) {
          console.error('Error creating same-location picture symbol:', error);
          return null;
        }
      })();

      picturePromises.push(picturePromise);
    });

    // Process individual pictures with direction markers
    individualPictures.forEach((picture, index) => {
      const picturePromise = (async () => {
        try {
          // Support multiple field names: thumb_url, original_url, preview, preview_url
          const imageUrl = picture.thumb_url || picture.original_url || picture.preview || picture.preview_url;
          const circularImageUrl = await this.imageProcessingService.createCircularImage(imageUrl, 35);

          const pictureId = picture.id || picture.uuid;
          // Normalize picture data to ensure thumb_url/original_url are set for the overlay
          const normalizedPicture = {
            ...picture,
            thumb_url: picture.thumb_url || picture.preview || picture.preview_url,
            original_url: picture.original_url || picture.original || picture.preview || picture.preview_url
          };
          this.markerPicturesMap.set(pictureId, [normalizedPicture]);

          const point = {
            type: 'point',
            longitude: parseFloat(picture.longitude),
            latitude: parseFloat(picture.latitude)
          };

          const Graphic = (await import('@arcgis/core/Graphic')).default;
          return new Graphic({
            geometry: point as any,
            symbol: this.mapSymbolFactory.createPictureMarkerSymbol(
              circularImageUrl,
              picture.direction || 0,
              pictureId
            ) as any,
            attributes: {
              ObjectID: sameLocationGroups.length + index + 1,
              uuid: pictureId,
              markerType: 'picture',
              is_same_location: 0,
              count: 1,
              line_ids: this.toLineIdsAttribute([picture]),
              fade: 0
            }
          });
        } catch (error) {
          console.error('Error creating picture symbol:', error);
          return null;
        }
      })();

      picturePromises.push(picturePromise);
    });

    // Wait for all picture graphics to be created
    const pictureGraphics = (await Promise.all(picturePromises)).filter(g => g !== null);

    if (pictureGraphics.length > 0) {
      const FeatureLayer = (await import('@arcgis/core/layers/FeatureLayer')).default;

      // Create unique value renderer based on uuid
      const uniqueValueInfos = pictureGraphics.map((graphic: any) => ({
        value: graphic.attributes.uuid,
        symbol: graphic.symbol
      }));

      // fade = 1 marks photos of lines other than the open/hovered one (set via applyEdits).
      // Opacity visual variables keep working with clustering; featureEffect does not.
      const OpacityVariable = (await import('@arcgis/core/renderers/visualVariables/OpacityVariable')).default;
      const fadeStops = [{ value: 0, opacity: 1 }, { value: 1, opacity: 0.3 }];

      const renderer = {
        type: 'unique-value',
        field: 'uuid',
        uniqueValueInfos: uniqueValueInfos,
        visualVariables: [new OpacityVariable({ field: 'fade', stops: fadeStops })]
      };

      const clusterSymbol = {
        type: 'simple-marker' as const,
        style: 'circle' as const,
        color: '#1036D6',
        size: '24px',
        outline: {
          color: '#FFFFFF',
          width: 2
        }
      };

      this.tempFilteredPictureLayer = new FeatureLayer({
        id: 'temp-filtered-picture-layer',
        source: pictureGraphics,
        objectIdField: 'ObjectID',
        fields: [
          { name: 'ObjectID', type: 'oid' },
          { name: 'uuid', type: 'string' },
          { name: 'markerType', type: 'string' },
          { name: 'is_same_location', type: 'integer' },
          { name: 'count', type: 'integer' },
          { name: 'picture_uuids', type: 'string' },
          { name: 'line_ids', type: 'string' },
          { name: 'fade', type: 'integer' }
        ],
        renderer: renderer as any,
        opacity: 1,
        visible: true,
        featureReduction: {
          type: 'cluster',
          clusterRadius: '60px',
          clusterMinSize: '24px',
          clusterMaxSize: '60px',
          symbol: clusterSymbol,
          // A cluster stays at full strength if any of its photos is not faded
          fields: [
            { name: 'fade_min', onStatisticField: 'fade', statisticType: 'min' },
            { name: 'uuid_mode', onStatisticField: 'uuid', statisticType: 'mode' }
          ],
          // A cluster renderer also draws single-photo "clusters", so keep their photo symbol
          renderer: {
            type: 'unique-value' as const,
            valueExpression: 'IIF($feature.cluster_count == 1, $feature.uuid_mode, "cluster")',
            uniqueValueInfos: uniqueValueInfos.map(info => ({ ...info, symbol: info.symbol.clone() })),
            defaultSymbol: clusterSymbol,
            visualVariables: [new OpacityVariable({ field: 'fade_min', stops: fadeStops })]
          },
          labelingInfo: [{
            deconflictionStrategy: 'none',
            labelExpressionInfo: { expression: 'Text($feature.cluster_count, "#,###")' },
            symbol: {
              type: 'text',
              color: '#FFFFFF',
              font: { family: 'Montserrat', size: 12, weight: 'bold' }
            },
            labelPlacement: 'center-center'
          }]
        }
      });

      // NOTE: Picture data is already stored in markerPicturesMap at line ~690 with full URLs
      // DO NOT overwrite here with graphic.attributes which lacks URL data

      // Add to map below unified markers
      if (context.mapView?.map) {
        const unifiedLayerIndex = context.unifiedMarkersLayer
          ? context.mapView.map.layers.indexOf(context.unifiedMarkersLayer)
          : -1;

        if (unifiedLayerIndex !== -1) {
          context.mapView.map.add(this.tempFilteredPictureLayer, unifiedLayerIndex);
        } else {
          context.mapView.map.add(this.tempFilteredPictureLayer);
        }

        // Wait for layer to be ready and refresh to ensure correct rendering at current zoom
        await this.tempFilteredPictureLayer.when();
        const layerView = await context.mapView.whenLayerView(this.tempFilteredPictureLayer);
        await layerView.when();
        this.tempFilteredPictureLayer.refresh();
      }
    }
  }

  /**
   * Filter map layers to show only elements related to selected item
   */
  async filterLayersForItem(
    bidItemData: any,
    context: DrilldownContext,
    config: DrilldownConfig
  ): Promise<void> {
    if (!context.mapView) {
      return;
    }

    const { isActivity, isDelivery, isForm } = this.determineItemType(bidItemData);
    const itemId = bidItemData?.id;

    if (!itemId) {
      return;
    }

    // Collect pictures
    const { pictureIds, noLocationPictures, picturesWithLocation } = this.collectPictures(
      bidItemData,
      isActivity,
      isDelivery,
      isForm
    );

    // Store no-location pictures for display
    this.drilldownNoLocationPictures = noLocationPictures;
    this.drilldownNoLocationCount = noLocationPictures.length;

    // Check if picture exists at same location as marker
    const hasPictureAtMarkerLocation = this.hasPictureAtSameLocation(bidItemData, picturesWithLocation);

    // Apply filters to feature layers
    this.hiddenLayerIds = [];
    if (context.mapView.map && context.mapView.map.layers) {
      context.mapView.map.layers.forEach((layer: any) => {
        // For unified markers layer
        if (layer.id === 'unified-markers-layer') {
          const filterExpression = this.buildFilterExpression(
            itemId,
            isActivity,
            isDelivery,
            hasPictureAtMarkerLocation,
            isForm
          );
          this.filterUnifiedLayer(layer, filterExpression);
        }
        // Hide other marker layers (but not the dedicated station-range layer —
        // that's the drilled item's own range and must stay visible in drilldown).
        else if (layer.id && !layer.id.includes('asBuilt') && !layer.id.includes('siteBoundary') && layer.id !== 'station-range-layer') {
          layer.visible = false;
          this.hiddenLayerIds.push(layer.id);
        }
      });
    }

    // Create filtered as-built layer for as-built and station-range graphics.
    await this.createFilteredAsBuiltLayer(bidItemData, context);

    // Create filtered picture layer
    if (pictureIds.length > 0) {
      await this.createFilteredPictureLayer(picturesWithLocation, context);
    }

    // Keep site boundaries visible but at 40% opacity
    if (context.projectBoundaryLayer) {
      context.projectBoundaryLayer.opacity = 0.4;
    }

    // Ensure unified markers layer is always on top
    if (context.mapView?.map && context.unifiedMarkersLayer) {
      const layers = context.mapView.map.layers;
      const unifiedLayerIndex = layers.indexOf(context.unifiedMarkersLayer);
      if (unifiedLayerIndex !== -1) {
        context.mapView.map.reorder(context.unifiedMarkersLayer, layers.length - 1);
      }
    }
  }

  /**
   * Restore all map layers to normal visibility
   * (Kept for backwards compatibility - delegates to helper methods)
   */
  async restoreAllLayers(
    context: DrilldownContext,
    config: DrilldownConfig
  ): Promise<void> {
    await this.removeTempLayers(context);
    await this.restoreLayerSettings(context, config);
  }

  /**
   * Get clustering configuration for unified layer
   */
  private getClusteringConfig(): any {
    return {
      type: 'cluster',
      clusterRadius: '80px',
      clusterMinSize: '24px',
      clusterMaxSize: '60px',
      clusterTransitionDuration: 200,
      clusterTransitionDistance: 10,
      filter: {
        where: "markerType != 'multi_item_location'"
      } as any,
      symbol: {
        type: 'simple-marker',
        style: 'circle',
        size: '32px',
        color: '#1036D6',
        outline: {
          color: 'transparent',
          width: 0
        }
      } as any,
      clusterProperties: {
        total_count: {
          onStatisticField: 'count',
          statisticType: 'sum'
        },
        picture_count: {
          onStatisticField: 'count',
          onStatisticExpression: "IIF($feature.markerType == 'picture', $feature.count, 0)",
          statisticType: 'sum'
        },
        bid_item_count: {
          onStatisticField: 'count',
          onStatisticExpression: "IIF($feature.markerType == 'bid_item', $feature.count, 0)",
          statisticType: 'sum'
        }
      } as any,
      labelingInfo: [{
        deconflictionStrategy: 'none',
        labelExpressionInfo: {
          expression: 'Text($feature.cluster_count, "#,###")'
        },
        symbol: {
          type: 'text',
          color: [255, 255, 255, 255],
          haloColor: [0, 0, 0, 0],
          haloSize: 0,
          font: {
            family: 'Montserrat',
            size: 14,
            weight: 'bold'
          }
        },
        labelPlacement: 'center-center',
        minScale: 0,
        maxScale: 0
      }]
    };
  }

  /**
   * Zoom to extent of all elements related to the item
   */
  async zoomToItemExtent(
    bidItemData: any,
    context: DrilldownContext,
    config: DrilldownConfig
  ): Promise<void> {
    if (!context.mapView) {
      return;
    }

    const itemId = bidItemData?.id;
    if (!itemId) {
      return;
    }

    // Ranged items (those with a usable station_range) are zoomed by the dedicated
    // station-range layer (sites-location-map.drawStationRangeForItem → zoomTo-
    // StationRangeExtent), which runs right after enterDrilldownMode. Doing a second
    // goTo here just gets immediately interrupted (view:goto-interrupted) and the
    // two competing zoom levels thrash tile queries on the unified FeatureLayer
    // (the [esri … executeQuery] tile errors). Let the station-range layer own it.
    if (this.hasUsableStationRange(bidItemData)) {
      return;
    }

    const { isActivity, isDelivery, isForm } = this.determineItemType(bidItemData);
    const extents: any[] = [];

    // Collect unified layer marker extents
    if (context.unifiedMarkersLayer) {
      try {
        const query = context.unifiedMarkersLayer.createQuery();
        const whereClause = isActivity
          ? `activity_id = '${itemId}'`
          : isDelivery
            ? `delivery_id = '${itemId}'`
            : isForm
              ? `form_id = '${itemId}'`
              : `report_bid_item_id = '${itemId}'`;
        query.where = whereClause;
        query.returnGeometry = true;
        const results = await context.unifiedMarkersLayer.queryFeatures(query);
        results.features.forEach((feature: any) => {
          if (feature.geometry) {
            extents.push(feature.geometry.extent || feature.geometry);
          }
        });
      } catch (error) {
        console.warn('Could not query unified markers:', error);
      }
    }

    // Collect GPS as-built extents from temp filtered layer (station ranges are
    // NOT here — they have their own dedicated layer that owns its own zoom).
    if (this.tempFilteredAsBuiltLayer) {
      this.tempFilteredAsBuiltLayer.graphics.toArray().forEach((graphic: any) => {
        if (graphic.geometry) {
          extents.push(graphic.geometry.extent || graphic.geometry);
        }
      });
    }

    // Collect picture extents from temp filtered picture layer
    if (this.tempFilteredPictureLayer) {
      try {
        const query = this.tempFilteredPictureLayer.createQuery();
        query.where = '1=1';
        query.returnGeometry = true;
        const results = await this.tempFilteredPictureLayer.queryFeatures(query);
        results.features.forEach((feature: any) => {
          if (feature.geometry) {
            extents.push(feature.geometry.extent || feature.geometry);
          }
        });
      } catch (error) {
        console.warn('Could not query temp picture layer:', error);
      }
    }

    // Combine all extents and zoom
    if (extents.length > 0) {
      const properExtents = this.processExtentsForZoom(extents);

      let combinedExtent = properExtents[0];
      for (let i = 1; i < properExtents.length; i++) {
        if (combinedExtent.union) {
          combinedExtent = combinedExtent.union(properExtents[i]);
        }
      }

      // Fit the marker + GPS as-builts with a small margin. Station ranges are
      // handled by the dedicated station-range layer's own zoom.
      const expansionFactor = 1.3;
      const expandedExtent = combinedExtent.expand ? combinedExtent.expand(expansionFactor) : combinedExtent;

      // Account for side panel offset in fullscreen mode
      let targetExtent = expandedExtent;
      if (config.isFullScreen) {
        const sidePanelWidth = 400;
        const viewWidth = context.mapView.width;
        const extentWidth = expandedExtent.width || (expandedExtent.xmax - expandedExtent.xmin);

        const shiftAmount = (sidePanelWidth / viewWidth) * extentWidth * 0.5;

        const shiftedExtent = expandedExtent.clone();
        shiftedExtent.xmin -= shiftAmount;
        shiftedExtent.xmax -= shiftAmount;
        targetExtent = shiftedExtent;
      }

      const leftPadding = config.isFullScreen ? 452 : 0;

      context.mapView.goTo({
        target: targetExtent
      }, {
        duration: 1000,
        easing: 'ease-in-out',
        padding: { top: 120, left: leftPadding, right: 0, bottom: 0 }
      } as any);
    }
  }

  /**
   * Process extents for zoom, handling mixed coordinate systems
   */
  private processExtentsForZoom(extents: any[]): any[] {
    const getXCoord = (geom: any): number | null => {
      if (geom.type === 'point' && geom.x !== undefined) return geom.x;
      if (geom.xmin !== undefined) return geom.xmin;
      if (geom.extent?.xmin !== undefined) return geom.extent.xmin;
      return null;
    };

    const isWebMercatorCoord = (x: number | null): boolean => x !== null && Math.abs(x) > 180;
    const isGeographicCoord = (x: number | null): boolean => x !== null && Math.abs(x) <= 180;

    const webMercatorCount = extents.filter(g => isWebMercatorCoord(getXCoord(g))).length;
    const geographicCount = extents.filter(g => isGeographicCoord(getXCoord(g))).length;

    return extents
      .filter((geom) => {
        const xCoord = getXCoord(geom);
        const isGeographic = isGeographicCoord(xCoord);

        // If mixed coordinate systems, keep only geographic ones
        if (webMercatorCount > 0 && geographicCount > 0) {
          return isGeographic;
        }

        return true;
      })
      .map((geom) => {
        if (geom.type === 'point') {
          // For points, create a minimal extent around them
          const buffer = 0.00001;
          return new Extent({
            xmin: geom.x - buffer,
            xmax: geom.x + buffer,
            ymin: geom.y - buffer,
            ymax: geom.y + buffer,
            spatialReference: geom.spatialReference
          });
        } else if (geom.extent) {
          return geom.extent;
        } else {
          return geom;
        }
      });
  }

  /**
   * Get temporary filtered picture layer (for external click handling)
   */
  getTempFilteredPictureLayer(): any {
    return this.tempFilteredPictureLayer;
  }

  /**
   * Get temporary filtered as-built layer (for line item hover effect)
   */
  getTempFilteredAsBuiltLayer(): any {
    return this.tempFilteredAsBuiltLayer;
  }
}
